/**
 * Contexte de session : profil de l'utilisateur connecté et réglages serveur.
 *
 * Les deux sont chargés une seule fois, au démarrage de l'application. Si la
 * session est absente, le client API redirige de lui-même la page entière vers
 * l'ouverture de session OIDC : il n'y a donc pas d'état « non connecté » à
 * gérer ici.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import {
  getCurrentUser,
  getSettings,
  type CurrentUserResponse,
  type SettingsResponse,
} from '../api';
import { messageOf } from './useAsync';

/** Contenu du contexte de session. */
export interface SessionValue {
  user: CurrentUserResponse;
  settings: SettingsResponse;
}

const SessionContext = createContext<SessionValue | null>(null);

/** État interne du fournisseur. */
type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; value: SessionValue }
  | { status: 'error'; message: string };

/** Écran d'attente affiché le temps de résoudre la session. */
function SessionSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="w-full max-w-md space-y-4 px-6">
        <div className="h-8 w-2/3 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <p className="sr-only">Chargement de votre session…</p>
      </div>
    </div>
  );
}

/** Charge la session et fournit le contexte à toute l'application. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    Promise.all([getCurrentUser(), getSettings()])
      .then(([user, settings]) => {
        if (!cancelled) setState({ status: 'ready', value: { user, settings } });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ status: 'error', message: messageOf(error) });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') return <SessionSkeleton />;

  if (state.status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
        <div className="max-w-lg rounded-lg border border-red-300 bg-red-50 p-6 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <h1 className="text-lg font-semibold">Impossible de charger votre session</h1>
          <p className="mt-2 text-sm">{state.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return <SessionContext.Provider value={state.value}>{children}</SessionContext.Provider>;
}

/** Accès à la session. Le fournisseur garantit qu'elle est résolue. */
export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession doit être utilisé à l’intérieur de <SessionProvider>.');
  }
  return value;
}
